import React, { useState } from 'react';
import { Layout, Sparkles, Building, Users, Target, Database, Globe, Languages, ArrowRight, Download, Code, AlertCircle, Loader, Lock, Zap, Eye } from 'lucide-react';
import { generateDesignMindMockup } from '../services/aiService';
import { t } from '../services/i18n';
import { GeneratedMockup, DesignMindUserInput, DesignMindLanguage, DesignMindAppState } from '../types/designmind';
import { User } from '../types';
import { MockupPreview } from './MockupPreview';

interface DesignMindAIProps {
  currentUser: User;
}

export const DesignMindAI: React.FC<DesignMindAIProps> = ({ currentUser }) => {
  const [appState, setAppState] = useState<DesignMindAppState>(DesignMindAppState.Input);
  const [mockupData, setMockupData] = useState<GeneratedMockup | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<DesignMindUserInput>({
    industry: '',
    businessModel: 'B2B SaaS',
    targetAudience: '',
    goals: '',
    cms: 'WordPress',
    competitors: '',
    language: 'en'
  });

  // Check if user is admin (admins have unlimited access)
  const isAdmin = currentUser.role === 'admin';
  
  // Check if user has paid plan
  const isPremium = currentUser.plan !== 'free' && 
                   currentUser.subscriptionExpiry && 
                   new Date(currentUser.subscriptionExpiry) > new Date();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Admins bypass all restrictions
    if (!isAdmin && !isPremium) {
      setErrorMsg(t('designmind.requiresUpgrade') || 'DesignMind AI is only available for paid plans. Please upgrade to access this feature.');
      setAppState(DesignMindAppState.Error);
      return;
    }

    // Admins bypass credit check
    if (!isAdmin && currentUser.credits < 10) {
      setErrorMsg(t('designmind.insufficientCredits', { credits: currentUser.credits.toString() }) || `Insufficient credits. DesignMind AI requires 10 credits per generation. You have ${currentUser.credits} credits.`);
      setAppState(DesignMindAppState.Error);
      return;
    }

    setAppState(DesignMindAppState.Generating);
    setErrorMsg(null);
    setLoading(true);

    try {
      const result = await generateDesignMindMockup(formData);
      
      // Ensure IDs are robust
      if (result.mockup.pages) {
        result.mockup.pages = result.mockup.pages.map((p: any, i: number) => ({
          ...p,
          id: p.id || `page-${i}`,
          sections: (p.sections || []).map((s: any, j: number) => ({
            ...s,
            id: s.id || `p${i}-s${j}`
          }))
        }));
      }
      
      setMockupData(result.mockup);
      setAppState(DesignMindAppState.Result);
      
      // Update user credits in parent component would be handled via callback
    } catch (err: any) {
      console.error(err);
      setErrorMsg(err.message || 'Failed to generate website mockup. Please try again.');
      setAppState(DesignMindAppState.Error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setAppState(DesignMindAppState.Input);
    setMockupData(null);
    setErrorMsg(null);
  };

  const handleExportJson = () => {
    if (!mockupData) return;
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(mockupData, null, 2));
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", `${mockupData.siteTitle.replace(/\s/g, '_')}_mockup.json`);
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  // Only Arabic is RTL - Darija uses Latin script, so it's LTR
  const isRTL = formData.language === 'ar';

  return (
    <div className="min-h-screen bg-[#0a0e27] text-white p-6" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 p-3 rounded-xl">
              <Layout className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">{t('designmind.title') || 'DesignMind AI - Website Mockup Generator'}</h1>
              <p className="text-slate-400">{t('designmind.subtitle') || 'Generate intelligent website mockups with SEO & conversion strategy'}</p>
            </div>
          </div>

          {/* Premium Badge or Warning */}
          {!isAdmin && !isPremium && (
            <div className="bg-amber-500/20 border border-amber-500/50 rounded-xl p-4 mb-6 flex items-center gap-3">
              <Lock className="w-5 h-5 text-amber-400" />
              <div>
                <p className="text-amber-300 font-semibold">{t('designmind.premiumFeature') || 'Premium Feature'}</p>
                <p className="text-amber-400/80 text-sm">{t('designmind.premiumDescription') || 'Upgrade to a paid plan to access DesignMind AI'}</p>
              </div>
            </div>
          )}

          {(isAdmin || isPremium) && (
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 mb-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-emerald-400" />
                  <span className="text-slate-300">{t('designmind.credits') || 'Credits'}:</span>
                  <span className="text-emerald-400 font-bold">{currentUser.credits}</span>
                </div>
                <span className="text-slate-500">|</span>
                <span className="text-slate-400 text-sm">{t('designmind.cost') || 'Cost: 10 credits per generation'}</span>
              </div>
            </div>
          )}
        </div>

        {/* Input State */}
        {appState === DesignMindAppState.Input && (
          <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 shadow-xl">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="text-indigo-400 w-5 h-5" />
                <h2 className="text-2xl font-bold text-white">{t('designmind.projectDefinition') || 'Project Definition'}</h2>
              </div>
              <p className="text-slate-400">{t('designmind.projectDescription') || 'Tell us about your business. Our AI will architect the perfect website structure.'}</p>
            </div>

            <form onSubmit={handleGenerate} className="space-y-6">
              {/* Language Selector */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-300">
                  <Languages className="w-4 h-4 text-indigo-400" /> {t('designmind.language') || 'Language'}
                </label>
                <select
                  name="language"
                  value={formData.language}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg bg-slate-900 border border-slate-700 text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none"
                >
                  <option value="en">English 🇺🇸</option>
                  <option value="fr">Français 🇫🇷</option>
                  <option value="es">Español 🇪🇸</option>
                  <option value="de">Deutsch 🇩🇪</option>
                  <option value="ar">العربية 🇸🇦 (RTL)</option>
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-300">
                    <Building className="w-4 h-4 text-indigo-400" /> {t('designmind.industry') || 'Industry / Niche'}
                  </label>
                  <input
                    required
                    name="industry"
                    value={formData.industry}
                    onChange={handleChange}
                    placeholder={t('designmind.placeholderIndustry')}
                    className="w-full px-4 py-3 rounded-lg bg-slate-900 border border-slate-700 text-white placeholder-slate-500 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-300">
                    <Users className="w-4 h-4 text-indigo-400" /> {t('designmind.businessModel') || 'Business Model'}
                  </label>
                  <select
                    name="businessModel"
                    value={formData.businessModel}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg bg-slate-900 border border-slate-700 text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none"
                  >
                    <option value="B2B SaaS">B2B SaaS</option>
                    <option value="B2C E-commerce">B2C E-commerce</option>
                    <option value="Local Service">Local Service / Lead Gen</option>
                    <option value="Consulting">Consulting / Agency</option>
                    <option value="Marketplace">Marketplace</option>
                    <option value="Non-Profit">Non-Profit</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-300">
                  <Target className="w-4 h-4 text-indigo-400" /> {t('designmind.targetAudience') || 'Target Audience'}
                </label>
                <input
                  required
                  name="targetAudience"
                  value={formData.targetAudience}
                  onChange={handleChange}
                  placeholder={t('designmind.placeholderAudience')}
                  className="w-full px-4 py-3 rounded-lg bg-slate-900 border border-slate-700 text-white placeholder-slate-500 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none"
                />
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-300">
                  <Sparkles className="w-4 h-4 text-indigo-400" /> {t('designmind.goals') || 'Primary Goals'}
                </label>
                <input
                  required
                  name="goals"
                  value={formData.goals}
                  onChange={handleChange}
                  placeholder={t('designmind.placeholderGoals')}
                  className="w-full px-4 py-3 rounded-lg bg-slate-900 border border-slate-700 text-white placeholder-slate-500 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-300">
                    <Database className="w-4 h-4 text-indigo-400" /> {t('designmind.cms') || 'CMS Platform'}
                  </label>
                  <select
                    name="cms"
                    value={formData.cms}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg bg-slate-900 border border-slate-700 text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none"
                  >
                    <option value="WordPress">WordPress</option>
                    <option value="Shopify">Shopify</option>
                    <option value="Webflow">Webflow</option>
                    <option value="React/Next.js">React / Next.js</option>
                    <option value="Wix/Squarespace">Wix / Squarespace</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-300">
                    <Globe className="w-4 h-4 text-indigo-400" /> {t('designmind.competitors') || 'Competitors (Optional)'}
                  </label>
                  <input
                    name="competitors"
                    value={formData.competitors}
                    onChange={handleChange}
                    placeholder={t('designmind.placeholderCompetitors')}
                    className="w-full px-4 py-3 rounded-lg bg-slate-900 border border-slate-700 text-white placeholder-slate-500 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || (!isAdmin && !isPremium) || (!isAdmin && currentUser.credits < 10)}
                className={`w-full py-4 rounded-xl text-white font-bold text-lg flex items-center justify-center gap-2 transition-all ${
                  loading || (!isAdmin && !isPremium) || (!isAdmin && currentUser.credits < 10)
                    ? 'bg-slate-700 cursor-not-allowed opacity-50' 
                    : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 transform hover:-translate-y-0.5 shadow-lg hover:shadow-xl'
                }`}
              >
                {loading ? (
                  <>
                    <Loader className="animate-spin w-5 h-5" />
                    {t('designmind.generating') || 'Generating Intelligent Mockup...'}
                  </>
                ) : (
                  <>
                    {t('designmind.generate') || 'Generate Mockup'} {formData.language !== 'ar' && <ArrowRight className="w-5 h-5" />}
                    {formData.language === 'ar' && <ArrowRight className="w-5 h-5 rotate-180" />}
                  </>
                )}
              </button>
            </form>
          </div>
        )}

        {/* Loading State */}
        {appState === DesignMindAppState.Generating && (
          <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-12 text-center">
            <Loader className="animate-spin w-12 h-12 text-indigo-400 mx-auto mb-4" />
            <p className="text-slate-300 text-lg">{t('designmind.generating') || 'Generating your intelligent website mockup...'}</p>
            <p className="text-slate-500 text-sm mt-2">This may take 60-90 seconds. Please be patient...</p>
            <div className="mt-4 flex justify-center">
              <div className="w-64 bg-slate-900 rounded-full h-2 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-600 animate-pulse" style={{ width: '60%' }}></div>
              </div>
            </div>
          </div>
        )}

        {/* Error State */}
        {appState === DesignMindAppState.Error && (
          <div className="bg-slate-800/50 border border-red-500/50 rounded-2xl p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">{t('designmind.errorTitle') || 'Generation Failed'}</h2>
            <p className="text-slate-400 mb-6">{errorMsg}</p>
            <button 
              onClick={handleReset} 
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              {t('designmind.tryAgain') || 'Try Again'}
            </button>
          </div>
        )}

        {/* Result State - Visual Preview */}
        {appState === DesignMindAppState.Result && mockupData && (
          <div className="space-y-6">
            {/* Result Header */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 flex items-center justify-between flex-wrap gap-4">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">{mockupData.siteTitle}</h2>
                <p className="text-slate-400">{t('designmind.pagesGenerated', { count: mockupData.pages.length.toString() }) || `${mockupData.pages.length} pages generated`}</p>
              </div>
              <div className="flex gap-3 flex-wrap">
                <button
                  onClick={handleExportJson}
                  className="flex items-center gap-2 bg-slate-700 text-white px-4 py-2 rounded-lg hover:bg-slate-600 transition-colors"
                >
                  <Download className="w-4 h-4" /> {t('designmind.exportJson') || 'Export JSON'}
                </button>
                <button
                  onClick={handleReset}
                  className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  {t('designmind.newProject') || 'New Project'}
                </button>
              </div>
            </div>

            {/* Visual Mockup Preview */}
            <div className="bg-slate-800/30 border border-slate-700 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <Eye className="w-5 h-5 text-indigo-400" />
                <h3 className="text-xl font-bold text-white">{t('designmind.visualPreview') || 'Visual Design Preview'}</h3>
              </div>
              <MockupPreview mockup={mockupData} language={formData.language} />
            </div>

            {/* JSON Data (Collapsible) */}
            <details className="bg-slate-900/50 border border-slate-700 rounded-2xl overflow-hidden">
              <summary className="cursor-pointer p-6 flex items-center justify-between hover:bg-slate-800/50 transition-colors">
                <div className="flex items-center gap-3">
                  <Code className="w-5 h-5 text-slate-400" />
                  <h3 className="text-lg font-bold text-white">{t('designmind.rawData') || 'Raw JSON Data'}</h3>
                </div>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    handleExportJson();
                  }}
                  className="flex items-center gap-2 text-indigo-400 hover:text-indigo-300 text-sm"
                >
                  <Download className="w-4 h-4" /> {t('designmind.downloadJson') || 'Download'}
                </button>
              </summary>
              <div className="p-6 border-t border-slate-700">
                <pre className="bg-slate-950 p-4 rounded-lg overflow-x-auto text-xs text-slate-300 border border-slate-800 max-h-96 overflow-y-auto">
                  {JSON.stringify(mockupData, null, 2)}
                </pre>
              </div>
            </details>
          </div>
        )}
      </div>
    </div>
  );
};

